import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export type ImportedSourceKind = "request" | "trace";

interface ImportedSourceIdsResponse {
  success?: boolean;
  source?: ImportedSourceKind;
  ids?: unknown;
}

export const useGetImportedSourceIds = ({
  tableId,
  sheetId,
  source,
  columnId,
  enabled = true,
}: {
  tableId?: string;
  sheetId?: string;
  source: ImportedSourceKind;
  columnId?: string | null;
  enabled?: boolean;
}) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);

  return useQuery<string[]>({
    queryKey: [
      "smart-tables",
      wid,
      tableId,
      sheetId,
      "imported-source-ids",
      source,
      columnId ?? null,
    ],
    queryFn: async () => {
      const response = await fetch(
        ENDPOINTS.smart_table_imported_source_ids(wid, tableId!, sheetId!, {
          source,
          columnId: columnId ?? undefined,
        }),
        {
          headers: jsonAuthHeaders(userToken),
        },
      );
      const payload = (await handleResponse(response)) as ImportedSourceIdsResponse;
      if (!Array.isArray(payload.ids)) {
        return [];
      }
      return payload.ids.map(String);
    },
    enabled: Boolean(enabled && workspaceId && tableId && sheetId),
    staleTime: 0,
    refetchOnMount: "always",
  });
};
